import moment from "moment";
import {getPercentages} from "../Colors/percentages";
import Color from 'color';

/**
 * Calculates the percentage of the score based on the given range.
 * Ensures the result is clamped between 15% and 100% for display purposes.
 *
 * @param {number} lowerRange - The lower bound of the range.
 * @param {number} higherRange - The upper bound of the range.
 * @param {number} score - The current score to be evaluated.
 * @returns {number} The calculated percentage.
 */
export const calculatePercentage = (lowerRange, higherRange, score) => {
  if (
    [lowerRange, higherRange, score].some(
      (item) => item === undefined || item === null
    )
  ) {
    return 0;
  }
  const val = (score - lowerRange) / (higherRange - lowerRange);
  const result = Math.max(15, val * 100); // Ensure minimum of 15%
  return Math.min(result, 100); // Ensure maximum of 100%
};

/**
 * Determines the range associated with a given color.
 *
 * @param {string} color - The color to determine the range for.
 * @returns {Array<number>} A two-element array with the lower and upper bounds of the range.
 */
export const determineRange = (color) => {
  if (!color) return [0, 0];

  switch (color.toLowerCase()) {
    case "green":
      return [0, 29];
    case "yellow":
      return [30, 55];
    case "red":
      return [56, 100];
    default:
      return [0, 0];
  }
};

/**
 * Rounds a number to one decimal place, adding precision without too much complexity.
 *
 * @param {number} num - The number to round.
 * @returns {number} The rounded number.
 */
export const round = (num) => {
  return Math.round((num + Number.EPSILON) * 10) / 10;
};

/**
 * Generates a string representation of a date in the format "YYYY-MM-DD".
 *
 * @param {Date} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const getDateString = (date) => {
  return moment(date).format("YYYY-MM-DD");
};

/**
 * Determines the style properties based on the information associated with a calendar date.
 * This includes the color for display, font color, background color, and shade color,
 * all determined based on the color coding and score in the `info` object.
 *
 * @param {object} info - The information object containing details about the calendar date, including color and score.
 * @returns {object} An object containing style properties for display, font, background, and shade colors.
 */
export const getColorStyles = (info, teamName, difficultyBarConfig) => {
  let borderColor, fontColor, backgroundColor, shadeColor, background;

  const { color } = info;

  const fillPercent = calculatePercentage(...determineRange(color), info.score);

  if (info["opponent"] != null) {
    // Game day logic
    fontColor = "white";
    switch (color) {
      case "red":
        borderColor = "#d9534f";
        backgroundColor = "#d9534f";
        shadeColor = "#d9534f66";
        break;
      case "high_yellow":
        borderColor = "#cc6600";
        backgroundColor = "#cc6600";
        shadeColor = "#cc660066";
        break;
      case "yellow":
        borderColor = "#FFC83D";
        backgroundColor = "#FFC83D";
        shadeColor = "#FFC83D66";
        break;
      case "green":
        borderColor = "#31B452";
        backgroundColor = "#31B452";
        shadeColor = "#31B45266";
        break;
      case "blue":
        borderColor = "#0096FF";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      default:
        borderColor = "#0096FF"; // Fallback color
        backgroundColor = "#FFFFFF"; // Fallback background color
        shadeColor = "#FFFFFF"; // Fallback shade color
    }
  } else {
    // Training day logic
    switch (color) {
      case "red":
        borderColor = "#d9534f";
        fontColor = "#d9534f";
        backgroundColor = "#FFE0E0";
        shadeColor = "#d9534f66";
        break;
      case "high_yellow":
        borderColor = "#cc6600";
        fontColor = "#cc6600";
        backgroundColor = "#FFF9D2";
        shadeColor = "#cc660066";
        break;
      case "yellow":
        borderColor = "#FFC83D";
        fontColor = "#FFC83D";
        backgroundColor = "#FFF9E0";
        shadeColor = "#FFC83D66";
        break;
      case "green":
        borderColor = "#31B452";
        fontColor = "#31B452";
        backgroundColor = "#E0FFE3";
        shadeColor = "#31B45266";
        break;
      case "blue":
        borderColor = "#0096FF";
        fontColor = "#0096FF";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      case "lime green":
        borderColor = "#94ff8a";
        fontColor = "#94ff8a";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      case "low orange":
        borderColor = "#ffce8a";
        fontColor = "#ffce8a";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      case "hot red":
        borderColor = "#FF51E2";
        fontColor = "#FF51E2";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      default:
        borderColor = "#0096FF"; // Fallback color
        fontColor = "#000"; // Fallback font color
        backgroundColor = "#FFFFFF"; // Fallback background color
        shadeColor = "#FFFFFF"; // Fallback shade color
    }
  }
  const { score } = info;
  
  // Use difficultyBarConfig to determine colors based on score segments
  if (score && difficultyBarConfig && difficultyBarConfig.colors && difficultyBarConfig.thresholds) {
    const { colors, thresholds } = difficultyBarConfig;
    
    // Normalize score to 0-1 range if it's in 0-100 range
    const normalizedScore = score > 1 ? score / 100 : score;
    
    // Find which segment the score falls into
    let segmentIndex = 0;
    for (let i = 0; i < thresholds.length; i++) {
      if (normalizedScore <= thresholds[i]) {
        segmentIndex = i;
        break;
      } else {
        segmentIndex = i + 1;
      }
    }
    
    // Get the color for this segment
    const segmentColor = colors[segmentIndex];
    
    if (segmentColor) {
      // Use the actual color from the config
      borderColor = segmentColor;
      
      // Create a lighter background color by adding opacity
      const baseColor = Color(segmentColor);
      backgroundColor = baseColor.alpha(0.2).string();
      shadeColor = baseColor.alpha(0.5).string();
      fontColor = baseColor.darken(0.3).string();
    }
  }

  if (info["score"]) {
    background = `linear-gradient(${backgroundColor} ${
      100 - fillPercent
    }%, ${shadeColor} ${100 - fillPercent}%, ${shadeColor} 100%)`;
  }

  if (info["opponent"]) {
    fontColor = "#000";
    borderColor = "#000";
    if (!info["score"]) {
      backgroundColor = "#f09892";
    }
  } else if (info["is_prediction"] && !info["total_time"]) {
    backgroundColor = "white";
    background = "white";
  }

  return {
    borderColor,
    fontColor,
    backgroundColor,
    fillPercent,
    background,
  };
};

export const RPE_METRICS = ["RPE", "session_RPE"];
const MAX_METRICS = ["top_speed", "accel_max", "decel_max"];

const canAverageMetric = (key) => {
  return !(
    ["distance_per_minute", "max_hr", "mean_hr"].includes(key) ||
    key.includes("per_min")
  );
};

export const getPredictionInfo = ({
  costPerMin,
  metrics,
  drills,
  dateString,
  data,
  practicePlan,
}) => {
  return metrics
    .filter(({ key }) => !RPE_METRICS.includes(key))
    .reduce(
      (cur, { key }) => ({
        ...cur,
        [key]: MAX_METRICS.includes(key)
          ? Math.max(
              ...costPerMin
                .filter((c) =>
                  practicePlan.map((d) => d.drill_id).includes(c.drill_id)
                )
                .map((o) => o?.[key] || 0)
            )
          : practicePlan.reduce(
              (cost, drill) =>
                cost +
                (canAverageMetric(key) ? drill.duration : 1) *
                  costPerMin.find((c) => c.drill_id === drill.drill_id)?.[key],
              0
            ) / (canAverageMetric(key) ? 1 : practicePlan.length || 1),
      }),
      {
        ...data[dateString],
        score: data[dateString]?.score,
        drills: practicePlan.map((drill) => ({
          name: drills.find((item) => item.drill_id === drill.drill_id).name,
          id: drill.drill_id,
          duration: drill.duration,
        })),
        prediction: true,
        date: dateString,
        is_prediction: true,
      }
    );
};

export const getMetricColor = (metric, value, colors) => {
  const { key } = metric;
  if (key === "distance") {
    value = round(value);
  } else {
    value = Math.round(value);
  }

  if (value > colors[key] * getPercentages(key).upper) {
    return "#d9534f";
  } else if (value > colors[key] * getPercentages(key).middle) {
    return "#EF7616";
  } else if (value > colors[key] * getPercentages(key).lower) {
    return "#FFC83D";
  } else if (value < colors[key] * getPercentages(key).lower) {
    return "#31B452";
  } else {
    return "black";
  }
};
